import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { PageHero, SectionLabel } from "../components/site/Bits";
import Reveal from "../components/site/Reveal";
import { GALLERY, BEFORE_AFTER, IMAGES } from "../data/content";

function BeforeAfter({ item }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-brandgreen font-semibold">{item.tag}</span>
          <h3 className="font-heading font-bold text-navy text-2xl tracking-tight">{item.title}</h3>
        </div>
        <span className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
          <MoveHorizontal className="w-4 h-4" /> Drag to compare
        </span>
      </div>
      <div
        ref={ref}
        data-testid={`before-after-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
        className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl select-none cursor-ew-resize"
        onMouseMove={(e) => e.buttons === 1 && move(e.clientX)}
        onClick={(e) => move(e.clientX)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
      >
        <img src={item.after} alt="After" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
        <span className="absolute bottom-3 right-3 rounded-full bg-brandgreen text-white text-xs font-semibold px-3 py-1">After</span>
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={item.before} alt="Before" className="absolute inset-0 h-full object-cover" style={{ width: ref.current ? ref.current.offsetWidth : "100%", maxWidth: "none" }} draggable="false" />
          <span className="absolute bottom-3 left-3 rounded-full bg-navy text-white text-xs font-semibold px-3 py-1">Before</span>
        </div>
        <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 grid place-items-center w-9 h-9 rounded-full bg-white shadow-lg">
            <MoveHorizontal className="w-4 h-4 text-navy" />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function Gallery() {
  return (
    <div data-testid="gallery-page">
      <PageHero
        crumb="Gallery"
        label="Our Work"
        title="See the Mark Clean difference."
        subtitle="Real results from homes and vehicles across Preston and Lancashire — swipe our before & afters and browse the gallery."
        image={IMAGES.heroInterior}
      />

      {/* Before / After */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><SectionLabel>Before &amp; After</SectionLabel></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
                Transformations worth showing off.
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-10 lg:grid-cols-2">
            {BEFORE_AFTER.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.1}>
                <BeforeAfter item={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Masonry gallery */}
      <section className="py-20 lg:py-28 bg-mist">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal><SectionLabel>Gallery</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 mb-12 font-heading font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
              A spotless portfolio.
            </h2>
          </Reveal>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {GALLERY.map((g, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group relative mb-5 break-inside-avoid overflow-hidden rounded-2xl"
                data-testid={`gallery-item-${i}`}
              >
                <img
                  src={g.url}
                  alt={g.caption}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <figcaption className="absolute bottom-4 left-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[transform,opacity] duration-300">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-brandgreen font-semibold">{g.cat}</span>
                  <p className="text-white font-heading font-semibold tracking-tight">{g.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground italic">
            Sample gallery — to be replaced with photos of your own completed jobs.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-navy text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-xl">
            Want results like these?
          </h2>
          <Link
            to="/book"
            data-testid="gallery-cta-book"
            className="inline-flex items-center gap-2 rounded-full bg-brandgreen px-8 py-4 font-semibold hover:bg-white hover:text-navy transition-colors duration-300 shrink-0"
          >
            Book Online <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
